import React, { useState, useEffect } from 'react';
import { brandService } from '../../services/brandService';
import Button from '../../components/ui/Button';
import { Plus, Loader2 } from 'lucide-react';
import BrandCard from './components/BrandCard';
import CreateBrandModal from './components/CreateBrandModal';

export default function BrandManagement() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await brandService?.getAll();
      setBrands(data);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading brands:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBrand = async (brandData) => {
    try {
      await brandService?.create(brandData);
      await loadBrands();
      setIsCreateModalOpen(false);
    } catch (err) {
      throw new Error(err.message);
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand? This action cannot be undone.')) {
      return;
    }

    try {
      await brandService?.delete(brandId);
      await loadBrands();
    } catch (err) {
      setError(err?.message);
      console.error('Error deleting brand:', err);
    }
  };

  const handleToggleActive = async (brandId) => {
    try {
      await brandService?.toggleActive(brandId);
      await loadBrands();
    } catch (err) {
      setError(err?.message);
      console.error('Error toggling brand status:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Brand Management</h1>
          <p className="text-gray-600 mt-2">Manage your brands and their social media profiles</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Add Brand
        </Button>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p className="font-semibold">Error</p>
          <p>{error}</p>
        </div>
      )}
      {brands?.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-4">No brands created yet</p>
          <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2 mx-auto">
            <Plus className="w-5 h-5" />
            Create Your First Brand
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands?.map((brand) => (
            <BrandCard
              key={brand?.id}
              brand={brand}
              onDelete={handleDeleteBrand}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}
      {isCreateModalOpen && (
        <CreateBrandModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateBrand}
        />
      )}
    </div>
  );
}